/* eslint-disable @typescript-eslint/no-explicit-any */
import { KeyboardEvent } from 'react';
import Icon from 'beek-design-system/Elements/Icon';
import styled from 'styled-components';

type InputProps = {
  placeholder: string;
  width?: string;
  icon: string;
  marginTop?: string;
  marginBottom?: string;
  marginLeft?: string;
  marginRight?: string;
  background?: string;
  landing?: boolean;
  colorPlaceholder?: string;
  onClickInput?: () => void;
  currentRefinement?: any;
  refine?: any;
  setQueryValue?: (arg: any) => void;
  clearQuery?: boolean;
  setClearQuery?: (arg: boolean) => void;
  queryValue?: string;
};

type MainContainerProps = {
  marginTop?: string;
  marginBottom?: string;
  marginLeft?: string;
  marginRight?: string;
};

type InputTextProps = {
  placeholder: string;
  width?: string;
  background?: string;
  landing?: boolean;
  colorPlaceholder?: string;
  queryValue?: string;
  searchClicked?: boolean;
};

const defaultProps = {
  marginTop: '0rem',
  marginBottom: '0rem',
  marginLeft: '0rem',
  marginRight: '0rem',
  width: '100%',
  background: 'var(--snow-blue)',
  landing: false,
  colorPlaceholder: 'var(--gray-70)',
  onClickInput: (): void => {},
  currentRefinement: null,
  refine: null,
  setQueryValue: (): void => {},
  clearQuery: false,
  setClearQuery: (): void => {},
  queryValue: null,
};

interface Dictionary {
  [index: string]: string;
}

const getColor = (colors: Dictionary, colorPlaceholder?: string): string => {
  if (colorPlaceholder) return colors[colorPlaceholder];
  return colors.gray70;
};

const InputText = styled.input<InputTextProps>`
  width: 100%;
  height: 2rem;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
  background-color: ${(props): string | undefined =>
    props.background ? props.background : 'var(--snow-blue)'};
  border: none;
  border-radius: 0.375rem;
  outline: none;
  padding-left: 2.375rem;
  font-family: var(--primary-font);
  @media ${({ theme }): string => theme.devices.mobileM} {
    height: 2rem;
  }
  @media ${({ theme }): string => theme.devices.mobileL} {
    height: 2rem;
  }
  @media ${({ theme }): string => theme.devices.tablet} {
    height: 2.5rem;
  }
  @media ${({ theme }): string => theme.devices.laptop} {
    padding-left: 3rem;
    height: 2.5rem;
  }
  ::placeholder {
    font-family: var(--primary-font);
    font-style: normal;
    font-weight: 400;
    font-size: 0.75rem;
    color: ${(props): string =>
      getColor(props.theme.colors, props.colorPlaceholder)};
    @media ${({ theme }): string => theme.devices.mobileM} {
      font-size: 0.875rem;
    }
  }
  :focus {
    border: 0.0625rem solid var(--beek-blue);
    border-bottom-left-radius: ${(props): string =>
      props.queryValue ? '0rem' : ''};
    border-bottom-right-radius: ${(props): string =>
      props.queryValue ? '0rem' : ''};
  }
`;
const MainContainer = styled.div<MainContainerProps>`
  position: relative;
  width: 100%;
  margin-top: ${(props): string | undefined => props.marginTop};
  margin-bottom: ${(props): string | undefined => props.marginBottom};
  margin-left: ${(props): string | undefined => props.marginLeft};
  margin-right: ${(props): string | undefined => props.marginRight};
`;
const IconContainer = styled.div`
  position: absolute;
  margin-top: 0.5831rem;
  margin-bottom: 1rem;
  margin-left: 0.5831rem;
  @media ${({ theme }): string => theme.devices.tablet} {
    margin-top: 0.75rem;
  }
  @media ${({ theme }): string => theme.devices.laptop} {
    margin-top: 0.7294rem;
    margin-bottom: 0.7294rem;
    margin-left: 1.208rem;
  }
`;

const Input = (props: InputProps): JSX.Element => {
  const {
    placeholder,
    width,
    icon,
    marginTop,
    marginBottom,
    marginRight,
    marginLeft,
    background,
    landing,
    colorPlaceholder,
    currentRefinement,
    refine,
    setQueryValue,
    clearQuery,
    setClearQuery,
    onClickInput,
    queryValue,
  } = props;
  const mainContainerStyles = {
    marginTop,
    marginBottom,
    marginRight,
    marginLeft,
  };
  const inputStyles = {
    placeholder,
    width,
    background,
    landing,
    colorPlaceholder,
  };
  if (clearQuery) {
    refine('');
    if (setClearQuery) setClearQuery(!clearQuery);
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
    } else if (event.key === 'Escape' && setClearQuery && setQueryValue) {
      setClearQuery(!clearQuery);
      setQueryValue('');
    }
  };

  return (
    <MainContainer {...mainContainerStyles} data-testid="input-wrapper">
      <IconContainer>
        <Icon colorVariant="gray70" name={icon} />
      </IconContainer>
      <InputText
        // ref={wrapperRef}
        queryValue={queryValue}
        onKeyDown={handleKeyDown}
        value={currentRefinement || ''}
        {...inputStyles}
        onChange={(event): void => {
          refine(event.currentTarget.value);
          if (setQueryValue) setQueryValue(event.currentTarget.value);
        }}
        onClick={(): void => {
          if (onClickInput) onClickInput();
        }}
        data-testid="input-text"
      />
    </MainContainer>
  );
};

Input.defaultProps = defaultProps;

export default Input;
